import { useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { fmtTL } from "../../lib/format";
import { FilterTh, useColFilters, isFilterActive } from "../ColFilters";
import { fmtSigned } from "./CorrectionsBlock";
import { groupRows, periodLabel } from "./claimGrouping";
import ClaimDetailPanel from "./ClaimDetailPanel";
import ClaimInvoiceLinks from "./ClaimInvoiceLinks";
import { invoicesForGroup } from "./useClaimInvoices";

export { periodLabel };

// Müşteri / Taşeron hakedişi için ortak sütun seti; tutar başlığı sayfaya göre
const makeCols = (amountLabel) => [
  { key: "period", label: "Dönem", type: "multi", fmt: periodLabel },
  { key: "project_name", label: "Proje" },
  { key: "region_name", label: "Bölge" },
  { key: "category_name", label: "Ana Hizmet" },
  { key: "total", label: amountLabel, type: "number" },
];

// Dönem · Proje · Bölge · Ana Hizmet satırları (alt hizmetlerin toplamı); tıklayınca hizmet/tarih/şube/miktar/tutar detayı açılır
export default function ClaimGroupedTable({ rows, corrections, summaryTotal, invoices = [], returnTo = "", testid = "customer-claim-groups", totalKey = "customer_total", unitKey = "customer_unit_price",
  amountLabel = "Müşteri Tutarı", unitLabel = "Müşteri Birim", totalLabel = "Toplam", rowTestid = "customer-claim-row", correctionsTestid = "customer-claim-corrections-footer", totalTestid = "customer-total" }) {
  const cols = useMemo(() => makeCols(amountLabel), [amountLabel]);
  const groups = useMemo(() => groupRows(rows, totalKey, corrections?.rows || []), [rows, totalKey, corrections]);
  const [hf, setHf, visible, autoOpts] = useColFilters(groups, cols);
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const filtering = cols.some((c) => isFilterActive(c, hf[c.key]));
  const visTotal = visible.reduce((a, g) => a + g.total, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={testid}>
      <table className="w-full tbl">
        <thead className="bg-slate-50">
          <tr>
            {cols.map((c, i) => (
              <th key={c.key} className={`px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold ${i === 4 ? "text-right" : "text-left"}`}>
                <FilterTh col={c} hf={hf} setHf={setHf} testid={`${testid}-hf-${c.key}`} align={i === 4 ? "right" : "left"} autoOptions={autoOpts[c.key]} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((g) => {
            const isOpen = open === g.key;
            return [
              <tr key={g.key} onClick={() => setOpen(isOpen ? null : g.key)} data-testid={`${testid}-row-${g.key}`} aria-expanded={isOpen}
                className={`border-t cursor-pointer transition-colors ${isOpen ? "border-amber-300 bg-amber-50" : "border-slate-100 hover:bg-slate-50"}`}>
                <td className="px-3 py-2 text-xs font-semibold text-slate-800"><span className="inline-flex items-center gap-1">{isOpen ? <ChevronDown className="w-3.5 h-3.5 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}{periodLabel(g.period)}</span></td>
                <td className="px-3 py-2 text-xs">{g.project_name}</td>
                <td className="px-3 py-2 text-xs">{g.region_name}</td>
                <td className="px-3 py-2 text-xs font-semibold">{g.category_name}<span className="ml-1.5 text-[10px] font-normal text-slate-400">({g.service_count} hizmet · {g.items.length} kayıt)</span>
                  {g.correction_count > 0 && <span className={`ml-1.5 px-1.5 py-0.5 rounded bg-violet-100 text-[10px] font-semibold ${g.correction_total < 0 ? "text-red-700" : "text-violet-900"}`} data-testid={`${testid}-correction-${g.key}`} title="Bu proje / bölge / ana hizmetin düzeltme farkları — satır toplamına dahil">düzeltme {fmtSigned(g.correction_total)}</span>}</td>
                <td className="numeric-cell px-3 py-2 text-xs font-semibold">
                  <ClaimInvoiceLinks invoices={invoicesForGroup(invoices, g)} returnTo={returnTo} testid={`${testid}-row-${g.key}`} />
                  <span data-testid={`${testid}-total-${g.key}`}>{fmtTL(g.total)}</span>
                </td>
              </tr>,
              isOpen && <ClaimDetailPanel key={`${g.key}-detail`} group={g} onClose={close} testid={`${testid}-row-${g.key}`} totalKey={totalKey} unitKey={unitKey} unitLabel={unitLabel} rowTestid={rowTestid} returnTo={returnTo} />,
            ];
          })}
          {visible.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-sm text-slate-500">{groups.length ? "Filtreye uyan kayıt yok" : "Kayıt yok"}</td></tr>}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200">
          {filtering && (
            <tr className="bg-amber-50/60" data-testid={`${testid}-filter-total`}>
              <td colSpan={4} className="px-3 py-2 text-xs font-semibold text-amber-900">Filtre Toplamı — {visible.length} grup / {visible.reduce((a, g) => a + g.items.length, 0)} kayıt</td>
              <td className="numeric-cell px-3 py-2 text-xs font-bold text-amber-900">{fmtTL(visTotal)}</td>
            </tr>
          )}
          {corrections?.count > 0 && (
            <tr className="bg-violet-50/40" data-testid={correctionsTestid}>
              <td colSpan={5} className="px-3 py-1.5 text-[11px] text-violet-900">Düzeltme farkları ({corrections.count} kayıt, {fmtSigned(corrections.total)}) ait olduğu proje / bölge / ana hizmet satırına yazıldı — genel toplama ayrıca eklenmez.</td>
            </tr>
          )}
          <tr>
            <td colSpan={4} className="px-3 py-3 text-xs font-semibold text-slate-700 uppercase tracking-wider">{totalLabel}{corrections?.count > 0 ? " (düzeltme farkları dahil)" : ""}</td>
            <td className="numeric-cell px-3 py-3 text-sm font-bold text-slate-900" data-testid={totalTestid}>{fmtTL(summaryTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
